import React, { useState, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort } from "@fortawesome/free-solid-svg-icons";

const TimeConflicts = () => {
  // ===== OPTION: Toggle this flag to use dummy data instead of computed data =====
  const useDummyData = false; // set to true to use the dummy dayData below

  // --- Schedule Visualization Setup ---
  const timeIntervals = [];
  for (let minutes = 8 * 60; minutes <= 17 * 60; minutes += 30) {
    timeIntervals.push(minutes);
  }

  const formatMinutes = (m) => {
    const hour = Math.floor(m / 60);
    const min = m % 60;
    const ampm = hour < 12 ? "AM" : "PM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${min.toString().padStart(2, "0")} ${ampm}`;
  };

  // Helper functions to parse time strings
  const parseTime = (time) => {
    const [timePart, ampm] = time.split(" ");
    const [hourStr, minuteStr] = timePart.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    if (ampm === "PM" && hour !== 12) {
      hour += 12;
    }
    if (ampm === "AM" && hour === 12) {
      hour = 0;
    }
    return hour * 60 + minute;
  };

  const parseTimeRange = (rangeStr) => {
    const [startStr, endStr] = rangeStr.split(" - ");
    return { start: parseTime(startStr), end: parseTime(endStr) };
  };

  // --- Dummy dayData Example (Pre-Computed) ---
  const dummyDayData = {
    Monday: [
      "free", "low", "moderate", "high", "free", "low", "moderate",
      "high", "free", "low", "moderate", "high", "free", "low", "moderate",
      "high", "free", "low", "free"
    ],
    Tuesday: [
      "low", "free", "low", "moderate", "high", "low", "free",
      "moderate", "high", "low", "free", "moderate", "high", "low",
      "free", "high", "moderate", "low", "free"
    ],
    Wednesday: [
      "moderate", "high", "free", "low", "moderate", "high", "free",
      "low", "moderate", "high", "free", "low", "moderate", "high",
      "free", "low", "moderate", "high", "free"
    ],
    Thursday: [
      "high", "moderate", "low", "free", "low", "moderate", "high",
      "free", "low", "moderate", "high", "free", "low", "moderate",
      "high", "free", "low", "moderate", "high"
    ],
    Friday: [
      "free", "low", "moderate", "high", "moderate", "low", "free",
      "free", "low", "moderate", "high", "moderate", "low", "free",
      "free", "low", "moderate", "high", "moderate"
    ],
  };

  // --- Courses Data for the Conflict Summary Table ---
  const courses = [
    {
      "course": "CMPT 307",
      "courseName": "Data Structures and Algorithms",
      "enrolledStudents": "22/40",
      "sectionsOffered": 2,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "9:30 AM - 10:45 AM"
        },
        {
          "days": ["Tu", "Th"],
          "time": "11:00 AM - 12:15 PM"
        }
      ],
      "overlap": "30%"
    },
    {
      "course": "CMPT 354",
      "courseName": "Database Systems I",
      "enrolledStudents": "16/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "2:00 PM - 3:15 PM"
        }
      ],
      "overlap": "25%"
    },
    {
      "course": "CMPT 363",
      "courseName": "User Interface Design",
      "enrolledStudents": "25/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "10:00 AM - 11:15 AM"
        }
      ],
      "overlap": "35%"
    },
    {
      "course": "CMPT 405",
      "courseName": "Design and Analysis of Algorithms",
      "enrolledStudents": "30/40",
      "sectionsOffered": 2,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "11:00 AM - 12:15 PM"
        },
        {
          "days": ["Tu", "Th"],
          "time": "2:00 PM - 3:15 PM"
        }
      ],
      "overlap": "40%"
    },
    {
      "course": "CMPT 454",
      "courseName": "Database Systems II",
      "enrolledStudents": "18/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["We", "Fr"],
          "time": "4:15 PM - 5:30 PM"
        }
      ],
      "overlap": "20%"
    },
    {
      "course": "CMPT 459",
      "courseName": "Data Mining",
      "enrolledStudents": "34/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "11:00 AM - 12:15 PM"
        }
      ],
      "overlap": "80%"
    },
    {
      "course": "CMPT 464",
      "courseName": "Introduction to Artificial Intelligence",
      "enrolledStudents": "26/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "10:00 AM - 11:15 AM"
        }
      ],
      "overlap": "35%"
    },
    {
      "course": "CMPT 470",
      "courseName": "Web-Based Information Systems",
      "enrolledStudents": "20/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "12:00 PM - 1:15 PM"
        }
      ],
      "overlap": "30%"
    },
    {
      "course": "CMPT 475",
      "courseName": "Computational Vision",
      "enrolledStudents": "28/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "11:00 AM - 12:15 PM"
        }
      ],
      "overlap": "40%"
    },
    {
      "course": "CMPT 479",
      "courseName": "Robotics",
      "enrolledStudents": "24/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "3:30 PM - 4:45 PM"
        }
      ],
      "overlap": "35%"
    },
    {
      "course": "CMPT 487",
      "courseName": "Deep Learning",
      "enrolledStudents": "32/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "1:00 PM - 2:15 PM"
        }
      ],
      "overlap": "70%"
    },
    {
      "course": "MACM 316",
      "courseName": "Numerical Analysis I",
      "enrolledStudents": "14/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "8:30 AM - 9:45 AM"
        }
      ],
      "overlap": "15%"
    },
    {
      "course": "MATH 310",
      "courseName": "Introduction to Ordinary Differential Equations",
      "enrolledStudents": "22/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "11:00 AM - 12:15 PM"
        }
      ],
      "overlap": "30%"
    },
    {
      "course": "MATH 320",
      "courseName": "Real Analysis I",
      "enrolledStudents": "18/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "2:00 PM - 3:15 PM"
        }
      ],
      "overlap": "25%"
    },
    {
      "course": "MATH 340",
      "courseName": "Group Theory",
      "enrolledStudents": "26/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "3:00 PM - 4:15 PM"
        }
      ],
      "overlap": "35%"
    },
    {
      "course": "MATH 345",
      "courseName": "Number Theory",
      "enrolledStudents": "20/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "11:00 AM - 12:15 PM"
        }
      ],
      "overlap": "30%"
    },
    {
      "course": "MATH 350",
      "courseName": "Geometry",
      "enrolledStudents": "24/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "9:30 AM - 10:45 AM"
        }
      ],
      "overlap": "35%"
    },
    {
      "course": "MATH 370",
      "courseName": "Applied Linear Algebra",
      "enrolledStudents": "28/40",
      "sectionsOffered": 2,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "2:00 PM - 3:15 PM"
        },
        {
          "days": ["Tu", "Th"],
          "time": "1:00 PM - 2:15 PM"
        }
      ],
      "overlap": "40%"
    },
    {
      "course": "MATH 380",
      "courseName": "Complex Variables",
      "enrolledStudents": "30/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "3:30 PM - 4:45 PM"
        }
      ],
      "overlap": "45%"
    },
    {
      "course": "MATH 390",
      "courseName": "Partial Differential Equations",
      "enrolledStudents": "16/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu"],
          "time": "2:00 PM - 5:30 PM"
        }
      ],
      "overlap": "25%"
    },
    {
      "course": "STAT 330",
      "courseName": "Introduction to Mathematical Statistics",
      "enrolledStudents": "18/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "9:30 AM - 10:45 AM"
        }
      ],
      "overlap": "30%"
    },
    {
      "course": "STAT 350",
      "courseName": "Linear Models in Applied Statistics",
      "enrolledStudents": "22/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "12:00 PM - 1:15 PM"
        }
      ],
      "overlap": "35%"
    },
    {
      "course": "STAT 380",
      "courseName": "Stochastic Processes",
      "enrolledStudents": "26/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We"],
          "time": "12:00 PM - 2:15 PM"
        }
      ],
      "overlap": "40%"
    },
    {
      "course": "STAT 410",
      "courseName": "Sample Surveys",
      "enrolledStudents": "20/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "3:00 PM - 4:15 PM"
        }
      ],
      "overlap": "30%"
    },
    {
      "course": "STAT 430",
      "courseName": "Experimental Design",
      "enrolledStudents": "24/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "2:00 PM - 3:15 PM"
        }
      ],
      "overlap": "35%"
    },
    {
      "course": "STAT 440",
      "courseName": "Learning from Big Data",
      "enrolledStudents": "28/40",
      "sectionsOffered": 2,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "9:30 AM - 10:45 AM"
        },
        {
          "days": ["Mo", "We", "Fr"],
          "time": "3:30 PM - 4:45 PM"
        }
      ],
      "overlap": "40%"
    },
    {
      "course": "STAT 452",
      "courseName": "Statistical Learning and Prediction",
      "enrolledStudents": "30/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "11:00 AM - 12:15 PM"
        }
      ],
      "overlap": "45%"
    },
    {
      "course": "STAT 460",
      "courseName": "Bayesian Statistics",
      "enrolledStudents": "16/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "2:00 PM - 3:15 PM"
        }
      ],
      "overlap": "25%"
    },
    {
      "course": "STAT 475",
      "courseName": "Applied Discrete Data Analysis",
      "enrolledStudents": "32/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Mo", "We", "Fr"],
          "time": "3:30 PM - 4:45 PM"
        }
      ],
      "overlap": "70%"
    },
    {
      "course": "STAT 485",
      "courseName": "Applied Time Series Analysis",
      "enrolledStudents": "32/40",
      "sectionsOffered": 1,
      "offeredOn": [
        {
          "days": ["Tu", "Th"],
          "time": "11:00 AM - 12:15 PM"
        }
      ],
      "overlap": "65%"
    }
  ];

  // --- Compute dayData from courses data if not using dummy data ---
  const computedDayData = useMemo(() => {
    const days = {
      Monday: Array(timeIntervals.length).fill(0),
      Tuesday: Array(timeIntervals.length).fill(0),
      Wednesday: Array(timeIntervals.length).fill(0),
      Thursday: Array(timeIntervals.length).fill(0),
      Friday: Array(timeIntervals.length).fill(0),
    };

    // Map abbreviated days to full names
    const dayMap = { Mo: "Monday", Tu: "Tuesday", We: "Wednesday", Th: "Thursday", Fr: "Friday" };

    courses.forEach((course) => {
      course.offeredOn.forEach((offer) => {
        const { start, end } = parseTimeRange(offer.time);
        offer.days.forEach((dayAbbrev) => {
          const fullDay = dayMap[dayAbbrev];
          if (fullDay && days[fullDay]) {
            days[fullDay] = days[fullDay].map((count, i) => {
              const slotMid = timeIntervals[i] + 15; // midpoint of the slot
              if (slotMid >= start && slotMid < end) {
                return count + 1;
              }
              return count;
            });
          }
        });
      });
    });

    // Convert count to a conflict level (thresholds can be adjusted)
    const countToConflictLevel = (count) => {
      if (count === 0) return "free";
      if (count === 1) return "low";
      if (count === 2) return "moderate";
      if (count === 3) return "high";
      return "severe";
    };

    for (const day in days) {
      days[day] = days[day].map(countToConflictLevel);
    }
    return days;
  }, [courses, timeIntervals]);

  // Choose which dayData to use
  const dayDataToUse = useDummyData ? dummyDayData : computedDayData;

  // Convert a conflict level to a Tailwind background class
  const getConflictColor = (level) => {
    const mapping = {
      free: "bg-gray-50",
      low: "bg-yellow-200",
      moderate: "bg-orange-300",
      high: "bg-red-500",
      severe: "bg-red-700",
    };
    return mapping[level] || "bg-gray-50";
  };

  const legendItems = [
    { label: "No conflict", color: "bg-gray-50" },
    { label: "Low", color: "bg-yellow-200" },
    { label: "Moderate", color: "bg-orange-300" },
    { label: "High", color: "bg-red-500" },
    { label: "Severe", color: "bg-red-700" },
  ];

  // Create schedule blocks from a day’s conflict array
  const createDayEvents = (conflictsArray) =>
    conflictsArray.map((level, idx) => {
      const start = timeIntervals[idx];
      const end = idx < timeIntervals.length - 1 ? timeIntervals[idx + 1] : start;
      return { start, end, conflictLevel: level };
    });

  const mondayEvents = createDayEvents(dayDataToUse.Monday);
  const tuesdayEvents = createDayEvents(dayDataToUse.Tuesday);
  const wednesdayEvents = createDayEvents(dayDataToUse.Wednesday);
  const thursdayEvents = createDayEvents(dayDataToUse.Thursday);
  const fridayEvents = createDayEvents(dayDataToUse.Friday);

  const [hoveredCell, setHoveredCell] = useState(null);
  const [highlightedCourse, setHighlightedCourse] = useState(null);
  const tableRef = useRef(null);

  // --- Sorting Logic for the Courses Table ---
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedCourses = useMemo(() => {
    let sortable = [...courses];
    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        let aValue, bValue;
        if (sortConfig.key === "offeredOn") {
          aValue = a.offeredOn
            .map((o) => `${o.days.join(", ")}: ${o.time}`)
            .join(" | ")
            .toLowerCase();
          bValue = b.offeredOn
            .map((o) => `${o.days.join(", ")}: ${o.time}`)
            .join(" | ")
            .toLowerCase();
        } else if (sortConfig.key === "overlap") {
          aValue = parseFloat(a.overlap);
          bValue = parseFloat(b.overlap);
        } else if (sortConfig.key === "enrolledStudents") {
          aValue = parseInt(a.enrolledStudents.split("/")[0]);
          bValue = parseInt(b.enrolledStudents.split("/")[0]);
        } else if (sortConfig.key === "sectionsOffered") {
          aValue = parseInt(a.sectionsOffered);
          bValue = parseInt(b.sectionsOffered);
        } else {
          aValue = a[sortConfig.key].toLowerCase();
          bValue = b[sortConfig.key].toLowerCase();
        }
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [courses, sortConfig]);

  // --- Dimensions for the Schedule Visualization ---
  const minTimeVal = 8 * 60;
  const rowHeight = 25;
  const pixelsPerMinute = rowHeight / 30;
  const timelineHeight = timeIntervals.length * rowHeight;
  const blockHeight = 22;
  const blockOffset = (rowHeight - blockHeight) / 2;

  // Mapping for day names and their abbreviations
  const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  const dayAbbrevMap = { Monday: "Mo", Tuesday: "Tu", Wednesday: "We", Thursday: "Th", Friday: "Fr" };

  // When a schedule block is clicked, find and highlight the first course offered at that time.
  const handleBlockClick = (dayIndex, event) => {
    const clickedDay = dayNames[dayIndex];
    const slotMid = event.start + 15; // midpoint of the block
    const clickedDayAbbrev = dayAbbrevMap[clickedDay];

    // Find the first course (from sortedCourses) that is offered on the clicked day at this time.
    const foundIndex = sortedCourses.findIndex((course) =>
      course.offeredOn.some((off) => {
        if (off.days.includes(clickedDayAbbrev)) {
          const { start, end } = parseTimeRange(off.time);
          return slotMid >= start && slotMid < end;
        }
        return false;
      })
    );

    if (foundIndex !== -1) {
      const element = document.getElementById(`course-${foundIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedCourse(foundIndex);
        setTimeout(() => setHighlightedCourse(null), 2000);
      }
    } else if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

    // Helper function to return a Flowbite tag based on the overlap percentage
    const renderOverlapTag = (overlapStr) => {
        const value = parseFloat(overlapStr);
        // Using w-24 (change as needed) and justify-center to center the text.
        const baseClasses =
          "inline-flex items-center justify-center w-14 ml-1 py-0.5 rounded text-xs font-medium";
        if (value >= 65) {
          return (
            <span className={`${baseClasses} bg-red-100 text-red-800`}>
              High
            </span>
          );
        } else if (value >= 35) {
          return (
            <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
              Medium
            </span>
          );
        } else {
          return (
            <span className={`${baseClasses} bg-green-100 text-green-800`}>
              Low
            </span>
          );
        }
      };
      
  

  return (
    <div className="max-h-[80vh] overflow-y-auto px-2">
      {/* Legend */}
      <div className="flex items-center space-x-4 mb-3">
        <span className="text-sm font-medium">Conflict-free</span>
        <div className="flex space-x-1">
          {legendItems.map((item, index) => (
            <div
              key={index}
              className={`w-6 h-6 rounded-md ${item.color} ${index === 0 ? "border border-gray-600" : ""}`}
            />
          ))}
        </div>
        <span className="text-sm font-medium">Highly Conflicting</span>
      </div>

      {/* Schedule Visualization */}
      <div className="border border-gray-300 relative max-h-[70vh] overflow-y-auto">
        <div className="grid grid-cols-[4rem_1fr]">
          <div className="border-b border-gray-300 bg-gray-100"></div>
          <div className="grid grid-cols-5">
            {dayNames.map((day) => (
              <div
                key={day}
                className="border-b border-l border-gray-300 bg-gray-100 text-center text-sm font-medium p-2"
              >
                {day}
              </div>
            ))}
          </div>
        </div>
        <div
          className="relative transition-all duration-500 ease-in-out"
          style={{ height: timelineHeight + "px" }}
        >
          <div className="grid grid-cols-[4rem_1fr] h-full">
            <div className="relative border-gray-300">
              {timeIntervals.map((time, i) => (
                <div
                  key={i}
                  className="absolute left-0 w-full text-xs text-gray-700 p-1"
                  style={{ top: (time - minTimeVal) * pixelsPerMinute + "px" }}
                >
                  {formatMinutes(time)}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-5 relative border-l border-gray-300">
              {[mondayEvents, tuesdayEvents, wednesdayEvents, thursdayEvents, fridayEvents].map(
                (dayEvents, dayIndex) => (
                  <div
                    key={dayIndex}
                    className={`relative ${dayIndex !== 4 ? "border-r border-gray-300" : ""}`}
                    style={{ height: timelineHeight }}
                  >
                    {dayEvents.map((event, idx) => {
                      const topPosition =
                        (event.start - minTimeVal) * pixelsPerMinute + blockOffset;
                      const isHovered =
                        hoveredCell &&
                        hoveredCell.dayIndex === dayIndex &&
                        hoveredCell.idx === idx;
                      return (
                        <div
                          key={idx}
                          onMouseEnter={() => setHoveredCell({ dayIndex, idx })}
                          onMouseLeave={() => setHoveredCell(null)}
                          onClick={() => handleBlockClick(dayIndex, event)}
                          className={`absolute left-1 right-1 rounded cursor-pointer transition-all duration-200 ${
                            isHovered ? "ring-2 ring-gray-400" : ""
                          } ${getConflictColor(event.conflictLevel)} ${
                            event.conflictLevel === "free" ? "border border-gray-300" : ""
                          }`}
                          style={{
                            top: topPosition + "px",
                            height: blockHeight + "px",
                          }}
                        ></div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Conflict Summary Table */}
      <div className="relative" ref={tableRef}>
        <table className="table-fixed border border-gray-300 mt-3 w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 z-10">
            <tr>
              <th
                className="pl-4 pr-2 pt-1.5 pb-1.5 cursor-pointer"
                style={{ width: "10%" }}
                onClick={() => handleSort("course")}
              >
                <div className="flex items-center justify-center gap-2">
                  <div className="text-center">
                    <div>Conflicting</div>
                    <div>Course</div>
                  </div>
                  <FontAwesomeIcon icon={faSort} className="ml-1" style={{ fontSize: "10px" }} />
                </div>
              </th>
              <th
                className="px-2 pt-1.5 pb-1.5 cursor-pointer text-center"
                style={{ width: "15%" }}
                onClick={() => handleSort("enrolledStudents")}
              >
                <div className="flex items-center justify-center gap-2">
                  <div className="text-center">
                    <div>Enrolled</div>
                    <div>Students</div>
                  </div>
                  <FontAwesomeIcon icon={faSort} className="ml-1" style={{ fontSize: "10px" }} />
                </div>
              </th>
              <th
                className="px-2 pt-1.5 pb-1.5 cursor-pointer text-center"
                style={{ width: "10%" }}
                onClick={() => handleSort("sectionsOffered")}
              >
                <div className="flex items-center justify-center gap-2">
                  <div className="text-center">
                    <div>Sections</div>
                    <div>Offered</div>
                  </div>
                  <FontAwesomeIcon icon={faSort} className="ml-1" style={{ fontSize: "10px" }} />
                </div>
              </th>
              <th
                className="px-2 pt-3 pb-1 cursor-pointer text-center"
                style={{ width: "25%" }}
                onClick={() => handleSort("offeredOn")}
              >
                Offered On{" "}
                <FontAwesomeIcon icon={faSort} className="ml-1" style={{ fontSize: "10px" }} />
              </th>
              <th
                className="px-2 pt-3 pb-1 cursor-pointer text-center"
                style={{ width: "15%" }}
                onClick={() => handleSort("overlap")}
              >
                Conflict %{" "}
                <FontAwesomeIcon icon={faSort} className="ml-1" style={{ fontSize: "10px" }} />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedCourses.map((item, idx) => {
              const offeredOnElements = item.offeredOn.map((o, index) => (
                <div key={index}>
                  {o.days.join(", ")}: {o.time}
                </div>
              ));
              return (
                <tr
                  key={idx}
                  id={`course-${idx}`}
                  className={`border-b transition-colors duration-1000 ${
                    highlightedCourse === idx ? "bg-blue-200" : "bg-white hover:bg-gray-50"
                  }`}
                >
                  <td
                    className="pl-4 pr-2 pt-2 pb-2 whitespace-nowrap text-sm text-gray-900 text-center align-middle"
                    style={{ width: "17%" }}
                  >
                    {item.course}
                  </td>
                  <td
                    className="px-2 pt-2 pb-2 whitespace-nowrap text-sm text-gray-900 text-center align-middle"
                    style={{ width: "11%" }}
                  >
                    {item.enrolledStudents}
                  </td>
                  <td
                    className="px-2 pt-2 pb-2 whitespace-nowrap text-sm text-gray-900 text-center align-middle"
                    style={{ width: "11%" }}
                  >
                    {item.sectionsOffered}
                  </td>
                  <td
                    className="px-2 pt-2 pb-2 whitespace-nowrap text-sm text-gray-900 text-center align-middle"
                    style={{ width: "20%" }}
                  >
                    {offeredOnElements}
                  </td>
                  <td
                    className="px-2 pt-2 pb-2 whitespace-nowrap text-sm text-gray-900 text-center align-middle"
                    style={{ width: "11%" }}
                  >
                    {item.overlap} {renderOverlapTag(item.overlap)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimeConflicts;
